import React, { useState } from 'react';
import { Modal, Button } from 'antd';
import commonS from 'styles/common.module.less';
import s from './s.module.less';

type Props = {
    open: boolean;
    payerName: string;
    state: string;
    signingUrl?: string;
    confirmLoading?: boolean;
    onClose: () => void;
    onConfirm: () => void;
};

const IFRAME_BLOCKED_HOSTS: string[] = [];

const isIframeBlocked = (url?: string): boolean => {
    if (!url) return true;
    try {
        const { hostname } = new URL(url);
        return IFRAME_BLOCKED_HOSTS.some((h) => hostname.endsWith(h));
    } catch {
        return false;
    }
};

const SignContractModal = ({ open, payerName, state, signingUrl, confirmLoading, onClose, onConfirm }: Props) => {
    const [iframeFailed, setIframeFailed] = useState(false);
    const useNewTab = iframeFailed || isIframeBlocked(signingUrl);

    const handleOpenSign = () => {
        if (signingUrl) {
            window.open(signingUrl, '_blank', 'noopener,noreferrer');
        }
    };

    const handleClose = () => {
        setIframeFailed(false);
        onClose();
    };

    return (
        <Modal
            open={open}
            onCancel={handleClose}
            width={useNewTab ? 520 : 920}
            className={useNewTab ? undefined : commonS.modalFixHeightWrap}
            title={
                <span>
                    {payerName} contract
                    {state && <span className={s.titleState}> &middot; {state}</span>}
                </span>
            }
            footer={[
                <Button key="close" onClick={handleClose}>
                    Close
                </Button>,
                <Button key="confirm" type="primary" loading={confirmLoading} onClick={onConfirm}>
                    I have signed it
                </Button>,
            ]}
            destroyOnClose
        >
            {!useNewTab && signingUrl ? (
                <>
                    <p className={s.instructionText}>
                        Sign your name where the contract asks. Use{' '}
                        <b style={{ color: '#111827' }}>Next required field</b> at the top right of the contract to jump
                        to each spot that still needs you.
                    </p>
                    <div className={s.embedWrap}>
                        <iframe
                            className={s.embedIframe}
                            src={signingUrl}
                            title={`${payerName} signing page`}
                            onError={() => setIframeFailed(true)}
                        />
                    </div>
                    <div className={s.fallbackRow}>
                        <span>
                            Trouble loading? {' · '}
                            <a href={signingUrl} target="_blank" rel="noopener noreferrer" className={s.fallbackLink}>
                                Open the contract in a new tab
                            </a>
                        </span>
                    </div>
                    <p className={s.doneHint}>
                        Once {payerName} shows that your signature is complete, click{' '}
                        <b style={{ color: '#111827' }}>I have signed it</b> below. Not finished? Close this and come
                        back anytime.
                    </p>
                </>
            ) : (
                <div className={s.stepList}>
                    <div className={s.step}>
                        <span className={s.stepNum}>1</span>
                        <div className={s.stepBody}>
                            <div className={s.stepTitle}>Open and sign the contract</div>
                            <div className={s.stepDetail}>
                                The contract opens in a new tab. Sign your name where the contract asks, then come back
                                here.
                            </div>
                            {signingUrl && (
                                <Button type="primary" className={s.openBtn} onClick={handleOpenSign}>
                                    Open contract to sign
                                </Button>
                            )}
                        </div>
                    </div>
                    <div className={s.step}>
                        <span className={s.stepNum}>2</span>
                        <div className={s.stepBody}>
                            <div className={s.stepTitle}>Confirm you signed it</div>
                            <div className={s.stepDetail}>
                                Once {payerName} shows that your signature is complete, click <b>I have signed it</b>{' '}
                                below. Not finished? Close this and come back anytime.
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </Modal>
    );
};

export default SignContractModal;
